import { QueryResult } from '@apollo/client';
import React, { useRef } from 'react';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

import { Exact, PayrollRunsFlatFileListQuery } from 'generated/graphql';

import { PayrollRunsTableRecents, PayrollRunsTableUpcoming } from './PayrollRunsTable';
import { CARD_WIDTH, PayrollRunInfo } from './PayrollRunInfo';
import { DonationsTable } from './DonationsTable';
import { URL_PARAM_PAYROLLRUN } from './consts';

type Props = {
  query: QueryResult<PayrollRunsFlatFileListQuery, Exact<{ payrollPayGroupId: string }>>;
  selectedPayGroup?: { payGroupDisplayName: string; id: string };
  hideLoading: boolean;
};

const GAP_WIDTH = 40;

export enum STATUSES {
  UPCOMING = 'upcoming',
  RECENT = 'recent',
}

const PayrollRunsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${GAP_WIDTH}px`,
};

const PayrollRunsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: '1px',
};

export const PayrollRuns = ({ query, selectedPayGroup, hideLoading }: Props) => {
  const payrollRunsContainer = useRef<HTMLDivElement>(null);

  return (
    <>
      {query.loading && !hideLoading && <LinearProgress />}
      {query.error && <Alert severity="error">The payroll runs could not be fetched</Alert>}
      {query.data?.payrollRunsFlatFile?.length === 0 && (
        <Alert severity="warning">
          There are no payroll runs in the pay group{' '}
          {selectedPayGroup && ` "${selectedPayGroup.payGroupDisplayName}"`}
        </Alert>
      )}
      {query.data?.payrollRunsFlatFile && selectedPayGroup && (
        <div style={PayrollRunsContainerStyle}>
          <div style={PayrollRunsStyle}>
            <Typography
              style={{
                fontSize: 18,
                fontWeight: 'bold',
                padding: 10,
              }}
            >
              Upcoming
            </Typography>
            <div ref={payrollRunsContainer}>
              <PayrollRunsTableUpcoming
                status={STATUSES.UPCOMING}
                payrollPayGroup={selectedPayGroup}
              />
            </div>
          </div>
          <div style={PayrollRunsStyle}>
            <Typography
              style={{
                fontSize: 18,
                fontWeight: 'bold',
                padding: 10,
              }}
            >
              Most recents
            </Typography>
            <div ref={payrollRunsContainer}>
              <PayrollRunsTableRecents
                status={STATUSES.RECENT}
                payrollPayGroup={selectedPayGroup}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CARDS_PER_PAGE = 4;
const SCROLL_BY = (GAP_WIDTH + CARD_WIDTH) * CARDS_PER_PAGE;
const SCROLL_BEFORE = -1;
const SCROLL_NEXT = 1;

const PayrollRunsContainerStyleV1: React.CSSProperties = {
  display: 'flex',
  marginBottom: '40px',
};

const PayrollRunsStyleV1: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  gap: `${GAP_WIDTH}px`,
  overflowX: 'auto',
  paddingBottom: '1px',
};

export const PayrollRunsV1 = ({ query, selectedPayGroup, hideLoading }: Props) => {
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const payrollRunSearchId = urlSearchParams.get(URL_PARAM_PAYROLLRUN);

  const payrollRunsContainer = useRef<HTMLDivElement>(null);
  const handleScroll = (multiplier: number) => {
    if (payrollRunsContainer.current) {
      payrollRunsContainer.current.scrollBy({
        left: multiplier * SCROLL_BY,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {query.loading && !hideLoading && <LinearProgress />}
      {query.error && <Alert severity="error">The payroll runs could not be fetched</Alert>}
      {query.data?.payrollRunsFlatFile?.length === 0 && (
        <Alert severity="warning">
          There are no payroll runs in the pay group{' '}
          {selectedPayGroup && ` "${selectedPayGroup.payGroupDisplayName}"`}
        </Alert>
      )}
      {query.data?.payrollRunsFlatFile && (
        <div style={PayrollRunsContainerStyleV1}>
          <IconButton
            color="primary"
            size="large"
            style={{
              marginLeft: '-45px',
              borderRadius: '16px 0 0 16px',
            }}
            onClick={() => handleScroll(SCROLL_BEFORE)}
          >
            <NavigateBefore />
          </IconButton>
          <div style={PayrollRunsStyleV1} ref={payrollRunsContainer}>
            {query.data?.payrollRunsFlatFile?.map((payrollRun) => (
              <PayrollRunInfo key={payrollRun.id} payrollRun={payrollRun} />
            ))}
          </div>
          <IconButton
            color="primary"
            size="large"
            style={{
              marginRight: '-45px',
              borderRadius: '0 16px 16px 0',
            }}
            onClick={() => handleScroll(SCROLL_NEXT)}
          >
            <NavigateNext />
          </IconButton>
        </div>
      )}
      {payrollRunSearchId && (
        <DonationsTable
          payrollRunFlatFileId={payrollRunSearchId}
          filters={{
            paymentStatuses: [],
            frequency: null,
            nonprofitTerm: null,
            employeeTerm: null,
            payrollPayGroupId: '123',
          }}
        />
      )}
    </>
  );
};
